'use client';

import { useEffect } from 'react';

/**
 * Global error boundary - replaces legacy /pages/_error and /pages/_document
 * fallbacks. Without this file, Next.js falls back to legacy `<Html>` document
 * rendering, which fails in App Router because there's no /pages/_document.
 *
 * Filed 2026-06-01 as part of Tier 3 SSR refactor.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[ScriptUnlock GlobalError]', error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#0a0e16',
          color: '#e8e8e8',
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}>
            Something went very wrong
          </h1>
          <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
            We&apos;ve been notified. Try refreshing or head back to the homepage.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: 12,
                background: '#0066ff',
                color: '#fff',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              Try Again
            </button>
            <a
              href="/"
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: 12,
                background: 'transparent',
                color: '#e8e8e8',
                border: '1px solid #2a2f3a',
                fontWeight: 600,
                textDecoration: 'none',
                minHeight: 48,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Go Home
            </a>
          </div>
          {error.digest ? (
            <p style={{ marginTop: 24, fontSize: '0.75rem', color: '#6b7280' }}>
              Error ID: {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  );
}
